import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { OpportunityCardDetails } from "../../shared/OpportunityCard";
import GeneratePostsButton from "./GeneratePostsButton";
import "bootstrap-icons/font/bootstrap-icons.css";

const GENERATE_NUDGE_MS = 3000;
const FADE = { duration: 0.35, ease: "easeInOut" };

function TrendActionButton({
    opportunity,
    isSelected,
    selectDisabled,
    showGenerateNudge,
    generateBusy,
    onToggleSelect,
    onGenerate,
    onSelectWithNudge,
}) {
    return (
        <div className="relative inline-grid min-h-[2.5rem] items-center justify-items-start">
            <AnimatePresence initial={false} mode="sync">
                {showGenerateNudge ? (
                    <motion.div
                        key="generate"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={FADE}
                        className="col-start-1 row-start-1"
                    >
                        <GeneratePostsButton
                            onClick={() => onGenerate?.()}
                            disabled={!onGenerate}
                            busy={generateBusy}
                        />
                    </motion.div>
                ) : (
                    <motion.button
                        key="select"
                        type="button"
                        onClick={() => {
                            if (!isSelected) {
                                onSelectWithNudge(opportunity);
                                return;
                            }
                            onToggleSelect(opportunity);
                        }}
                        disabled={selectDisabled && !isSelected}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={FADE}
                        className={`col-start-1 row-start-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                            isSelected
                                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                : "bg-orange-700 text-white hover:bg-orange-800 disabled:cursor-not-allowed disabled:opacity-40"
                        }`}
                    >
                        <i
                            className={`bi ${isSelected ? "bi-check2" : "bi-arrow-right"}`}
                            aria-hidden
                        />
                        {isSelected ? "Using This Trend" : "Use This Trend"}
                    </motion.button>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function OpportunityPickerItem({
    opportunity,
    isSelected = false,
    selectDisabled = false,
    onToggleSelect,
    onGenerate = null,
    generateBusy = false,
}) {
    const [expanded, setExpanded] = useState(false);
    const [showGenerateNudge, setShowGenerateNudge] = useState(false);
    const nudgeTimerRef = useRef(null);
    const { title, opportunity: opportunityText } = opportunity ?? {};
    const hasBody = Boolean(opportunityText);

    useEffect(() => {
        return () => {
            if (nudgeTimerRef.current) window.clearTimeout(nudgeTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!isSelected && showGenerateNudge) {
            setShowGenerateNudge(false);
            if (nudgeTimerRef.current) window.clearTimeout(nudgeTimerRef.current);
        }
    }, [isSelected, showGenerateNudge]);

    const handleSelectWithNudge = (item) => {
        onToggleSelect(item);
        setShowGenerateNudge(true);
        if (nudgeTimerRef.current) window.clearTimeout(nudgeTimerRef.current);
        nudgeTimerRef.current = window.setTimeout(() => {
            setShowGenerateNudge(false);
            nudgeTimerRef.current = null;
        }, GENERATE_NUDGE_MS);
    };

    const actionButton = (
        <TrendActionButton
            opportunity={opportunity}
            isSelected={isSelected}
            selectDisabled={selectDisabled}
            showGenerateNudge={showGenerateNudge}
            generateBusy={generateBusy}
            onToggleSelect={onToggleSelect}
            onGenerate={onGenerate}
            onSelectWithNudge={handleSelectWithNudge}
        />
    );

    return (
        <article className="rounded-xl border border-slate-200 bg-white p-4 transition hover:border-slate-300">
            {title ? (
                <h3 className="mb-1 text-base font-semibold leading-snug text-slate-900">
                    {title}
                </h3>
            ) : null}

            {hasBody ? (
                <div>
                    <p
                        className={`mb-0 text-sm leading-relaxed text-slate-600 ${
                            expanded ? "" : "line-clamp-2"
                        }`}
                    >
                        {opportunityText}
                    </p>
                    <button
                        type="button"
                        onClick={() => setExpanded((prev) => !prev)}
                        className="mt-0.5 p-0 text-sm font-semibold text-slate-800 underline decoration-slate-300 underline-offset-2 transition hover:text-slate-950 hover:decoration-slate-500"
                        aria-expanded={expanded}
                    >
                        {expanded ? "Hide details" : "View details"}
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setExpanded((prev) => !prev)}
                    className="mt-1 p-0 text-sm font-semibold text-slate-800 underline decoration-slate-300 underline-offset-2 transition hover:text-slate-950 hover:decoration-slate-500"
                    aria-expanded={expanded}
                >
                    {expanded ? "Hide details" : "View details"}
                </button>
            )}

            {!expanded ? (
                <div className="mt-3">{actionButton}</div>
            ) : (
                <div className="mt-4 border-t border-slate-100 pt-4">
                    <OpportunityCardDetails opportunity={opportunity} />
                    <div className="mt-4">{actionButton}</div>
                </div>
            )}
        </article>
    );
}
